/**
 * Wishlist Page
 * Displays user's saved/favorited strategies
 */
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  getWishlist,
  removeFromWishlist,
  formatPrice,
  getRatingStars,
  type MarketplaceStrategy,
} from '../services/marketplaceApi'
import LoadingSpinner from '../components/LoadingSpinner'

// ============================================================================
// Components
// ============================================================================

const StarRating = ({ rating, size = 14 }: { rating: number; size?: number }) => {
  const { full, half, empty } = getRatingStars(rating)
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(full)].map((_, i) => (
        <svg key={`full-${i}`} width={size} height={size} viewBox="0 0 20 20" fill="#f59e0b">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
      {[...Array(empty)].map((_, i) => (
        <svg key={`empty-${i}`} width={size} height={size} viewBox="0 0 20 20" fill="#e5e7eb">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  )
}

const WishlistCard = ({ 
  strategy, 
  onRemove,
  onClick 
}: { 
  strategy: MarketplaceStrategy
  onRemove: () => void
  onClick: () => void
}) => {
  const returnColor = strategy.metrics.total_return >= 0 ? 'text-green-400' : 'text-red-400'

  return (
    <div className="bg-gray-800 rounded-xl overflow-hidden hover:bg-gray-750 transition-all hover:shadow-xl">
      {/* Header */}
      <div className="p-4 border-b border-gray-700">
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1">
            {strategy.is_featured && (
              <span className="inline-block px-2 py-0.5 text-xs bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full mb-2">
                ⭐ Featured
              </span>
            )}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}
            className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
            title="Remove from wishlist"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
        <h3 className="font-semibold text-white line-clamp-1">{strategy.name}</h3>
        <p 
          className="text-sm text-primary hover:text-primary-dark cursor-pointer mt-1"
          onClick={(e) => {
            e.stopPropagation()
            window.location.href = `/creator/${strategy.creator_id}`
          }}
        >
          by {strategy.creator_name}
        </p>
      </div>

      {/* Metrics */}
      <div className="p-4 grid grid-cols-2 gap-3 cursor-pointer" onClick={onClick}>
        <div className="text-center">
          <div className="text-xs text-gray-400">Return</div>
          <div className={`text-lg font-bold ${returnColor}`}>
            {strategy.metrics.total_return >= 0 ? '+' : ''}{strategy.metrics.total_return.toFixed(1)}%
          </div>
        </div>
        <div className="text-center">
          <div className="text-xs text-gray-400">Win Rate</div>
          <div className="text-lg font-bold text-white">{strategy.metrics.win_rate.toFixed(1)}%</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-gray-400">Sharpe</div>
          <div className="text-lg font-bold text-white">{strategy.metrics.sharpe_ratio.toFixed(2)}</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-gray-400">Price</div>
          <div className="text-lg font-bold text-white">{formatPrice(strategy.price, strategy.pricing)}</div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 pb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StarRating rating={strategy.avg_rating} />
          <span className="text-sm text-gray-400">({strategy.total_ratings})</span>
        </div>
        <span className="px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded-lg">
          {strategy.category.replace('_', ' ')}
        </span>
      </div>
    </div>
  )
}

// ============================================================================
// Main Component
// ============================================================================

// Demo user ID - in production, this would come from auth context
const DEMO_USER_ID = 'user_demo'

export default function Wishlist() {
  const navigate = useNavigate()
  const [strategies, setStrategies] = useState<MarketplaceStrategy[]>([])
  const [loading, setLoading] = useState(true)
  const [removingId, setRemovingId] = useState<string | null>(null)

  useEffect(() => {
    const fetchWishlist = async () => {
      setLoading(true)
      try {
        const response = await getWishlist(DEMO_USER_ID)
        if (response.success && response.data) {
          setStrategies(response.data as unknown as MarketplaceStrategy[])
        }
      } catch (err) {
        console.error('Failed to fetch wishlist:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchWishlist()
  }, [])

  const handleRemove = async (strategyId: string) => {
    setRemovingId(strategyId)
    try {
      await removeFromWishlist(DEMO_USER_ID, strategyId)
      setStrategies(prev => prev.filter(s => s.strategy_id !== strategyId))
    } catch (err) {
      console.error('Failed to remove from wishlist:', err)
    } finally {
      setRemovingId(null)
    }
  }

  const handleStrategyClick = (strategy: MarketplaceStrategy) => {
    navigate(`/marketplace?strategy=${strategy.strategy_id}`)
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                <span className="text-red-400">❤️</span>
                My Wishlist
              </h1>
              <p className="text-gray-400 mt-1">
                {strategies.length} {strategies.length === 1 ? 'strategy' : 'strategies'} saved
              </p>
            </div>
            <button
              onClick={() => navigate('/marketplace')}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              Browse Marketplace
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {strategies.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {strategies.map(strategy => (
              <WishlistCard
                key={strategy.strategy_id}
                strategy={strategy}
                onRemove={() => handleRemove(strategy.strategy_id)}
                onClick={() => handleStrategyClick(strategy)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">💔</div>
            <h2 className="text-xl font-bold text-white mb-2">Your wishlist is empty</h2>
            <p className="text-gray-400 mb-6">
              Start exploring strategies and save your favorites here
            </p>
            <button
              onClick={() => navigate('/marketplace')}
              className="px-6 py-3 bg-primary hover:bg-primary-dark text-white font-semibold rounded-lg transition-colors"
            >
              Explore Marketplace
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
